import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import moment from 'moment';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import ManageBasemapDialog from 'app/components/DeclarativeMap/ManageBasemapDialog';
import ManageLayersDialog from 'app/components/DeclarativeMap/ManageLayersDialog';
import LensLogo from 'app/components/Logo/LensLogo';
import {ApiFeature, ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection, GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {MapSettingsTool} from 'app/pages/MonitorProjectView/view';
import {useFeatureDataForFeature} from 'app/providers/FeatureDataProvider';
import {GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import * as featureUtils from 'app/utils/featureUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';
import {resolveImageRefs} from 'app/utils/imageRefUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {imageRefsArentBasemap, overlayVisibilityByNameForNote} from 'app/utils/noteUtils';

import StoryCaption from './StoryCaption';
import StoryMap from './StoryMap';
import {StorySlide} from './storySlides';
import cs from './styles.module.scss';

const BAR_PADDING_PX = 60;
const EMBEDDED_LOGO_PADDING_PX = 80;
const SIDE_PADDING_PX = 60;
const CAPTION_CLEARANCE_PX = 48;

const LENS_MARKETING_URL = 'https://www.upstream.tech/lens';

interface Props {
  title: string;
  /** Null on a public story, which has no app to go back to and shows the Lens logo instead. */
  backUrl: string | null;
  /** Never empty: a loaded report always yields at least its cover. */
  slides: StorySlide[];
  skippedCount: number;
  /** Notes a public story leaves out because their imagery can't be shared. */
  unshareableCount?: number;
  /** Framed in another page: no story bar, just a logo over the map, as the public map does. */
  isEmbedded?: boolean;
  /** Zero-based, already clamped to `slides`. */
  slideIndex: number;
  onChangeSlide: (index: number) => void;
  isMultilocation: boolean;
  features: I.ImmutableOf<ApiFeature[]>;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
  overlaySettings: GeometryOverlaySetting[];
  defaultOverlayVisibility: Record<string, boolean>;
  geoJsonFeaturesLoader: GeoJsonFeaturesLoader;
  organization: I.ImmutableOf<ApiOrganization>;
  firebaseToken: string;
}

const StoryView: React.FunctionComponent<Props> = ({
  title,
  backUrl,
  slides,
  skippedCount,
  unshareableCount = 0,
  isEmbedded = false,
  slideIndex,
  onChangeSlide,
  isMultilocation,
  features,
  featureCollection,
  overlayFeatureCollections,
  overlaySettings,
  defaultOverlayVisibility,
  geoJsonFeaturesLoader,
  organization,
  firebaseToken,
}) => {
  const navigate = useNavigate();
  const slide = slides[slideIndex];
  const nextSlide: StorySlide | undefined = slides[slideIndex + 1];

  const featureData = useFeatureDataForFeature(slide.feature.get('id'));
  // Warms the next slide's imagery index so stepping forward doesn't wait on it.
  useFeatureDataForFeature(
    nextSlide && nextSlide.feature !== slide.feature ? nextSlide.feature.get('id') : null
  );

  // Sorted even before featureData arrives, so a compare slide's before/after
  // sides don't swap once it does. The copy keeps the note's own refs untouched.
  const imageRefs = React.useMemo<mapUtils.MapImageRefs>(
    () =>
      imageRefsArentBasemap(slide.imageRefs)
        ? resolveImageRefs([...slide.imageRefs] as mapUtils.MapImageRefs, featureData)
        : slide.imageRefs,
    [slide, featureData]
  );

  // Each slide starts from the author's overlay choices: the note's overlays,
  // then the report page's saved toggles. The viewer's own toggles last only
  // until the next slide.
  const overlays = React.useMemo(
    () =>
      overlayFeatureCollections
        .toArray()
        .map((fc) => ({id: fc.get('id') as number, name: fc.get('name') as string})),
    [overlayFeatureCollections]
  );
  const slideOverlayVisibility = React.useMemo(
    () =>
      slide.kind === 'note'
        ? {
            ...defaultOverlayVisibility,
            ...overlayVisibilityByNameForNote(slide.note, overlays),
            ...slide.overlayVisibilityByName,
          }
        : defaultOverlayVisibility,
    [defaultOverlayVisibility, overlays, slide]
  );
  const [isOverlayVisibleByName, setIsOverlayVisibleByName] = useStateWithDeps(
    slideOverlayVisibility,
    [slide.key]
  );
  const setOverlayVisibility = React.useCallback(
    (setting: Record<string, boolean>) =>
      setIsOverlayVisibleByName((visible) => ({...visible, ...setting})),
    [setIsOverlayVisibleByName]
  );

  const [localOverlaySettings, setLocalOverlaySettings] = useStateWithDeps(overlaySettings, [
    overlaySettings,
  ]);
  const [activeTool, setActiveTool] = React.useState<MapSettingsTool | null>(null);
  const [mapStyle, setMapStyle] = React.useState<mapUtils.MapStyle>(mapUtils.DEFAULT_STYLE);

  // Every slide opens with its caption expanded; collapsing is for exploring the map.
  const [isCaptionCollapsed, setIsCaptionCollapsed] = useStateWithDeps(false, [slide.key]);

  // Measured before paint, so a slide's first camera fit already knows how much
  // of the map its caption covers. Collapsing is never measured: it shouldn't
  // move the camera, and the next slide opens expanded anyway.
  const [captionHeight, setCaptionHeight] = useStateWithDeps<number | null>(null, [slide.key]);
  const captionRef = React.useRef<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    if (!isCaptionCollapsed && captionRef.current) {
      setCaptionHeight(captionRef.current.getBoundingClientRect().height);
    }
  }, [slide.key, isCaptionCollapsed, setCaptionHeight]);
  const fitPadding = React.useMemo<mapboxgl.PaddingOptions>(
    () => ({
      top: isEmbedded ? EMBEDDED_LOGO_PADDING_PX : BAR_PADDING_PX,
      right: SIDE_PADDING_PX,
      bottom: (captionHeight ?? 0) + CAPTION_CLEARANCE_PX,
      left: SIDE_PADDING_PX,
    }),
    [captionHeight, isEmbedded]
  );

  const goToPrevious = React.useCallback(() => {
    if (slideIndex > 0) {
      onChangeSlide(slideIndex - 1);
    }
  }, [slideIndex, onChangeSlide]);
  const goToNext = React.useCallback(() => {
    if (slideIndex < slides.length - 1) {
      onChangeSlide(slideIndex + 1);
    }
  }, [slideIndex, slides.length, onChangeSlide]);

  const hotkeys = React.useMemo<B.HotkeyConfig[]>(
    () => [
      {combo: 'left', global: true, label: 'Previous slide', onKeyDown: goToPrevious},
      {combo: 'right', global: true, label: 'Next slide', onKeyDown: goToNext},
    ],
    [goToPrevious, goToNext]
  );
  B.useHotkeys(hotkeys);

  return (
    <div className={cs.container}>
      {!isEmbedded && (
        <B.Navbar className={cs.storyBar}>
          <B.NavbarGroup align={B.Alignment.LEFT}>
            {backUrl ? (
              <B.Button minimal icon="arrow-left" text="Back" onClick={() => navigate(backUrl)} />
            ) : (
              <LensLogo
                className={cs.lensLogo}
                onClick={() => window.open(LENS_MARKETING_URL, '_blank')}
              />
            )}
            <B.NavbarDivider />
            <B.NavbarHeading className={cs.storyTitle} title={title}>
              {title}
            </B.NavbarHeading>
            {skippedCount > 0 && (
              <B.Tag
                minimal
                className={cs.skippedTag}
                title="Notes that were archived or can no longer be shown"
              >
                {skippedCount} {skippedCount === 1 ? 'note' : 'notes'} unavailable
              </B.Tag>
            )}
            {unshareableCount > 0 && (
              <B.Tag minimal className={cs.skippedTag}>
                Data from {unshareableCount} {unshareableCount === 1 ? 'note' : 'notes'} can’t be
                shared publicly
              </B.Tag>
            )}
          </B.NavbarGroup>
          <B.NavbarGroup align={B.Alignment.RIGHT}>
            <SceneChips
              imageRefs={imageRefs}
              featureData={featureData}
              organization={organization}
            />
            <B.NavbarDivider />
            <span className={cs.slideCount}>
              {slideIndex + 1} / {slides.length}
            </span>
          </B.NavbarGroup>
        </B.Navbar>
      )}

      <div className={cs.mapContainer}>
        {isEmbedded && (
          <div className={cs.embeddedLogo}>
            <LensLogo isWhite onClick={() => window.open(LENS_MARKETING_URL, '_blank')} />
          </div>
        )}
        <StoryMap
          slide={slide}
          imageRefs={imageRefs}
          featureData={featureData}
          features={features}
          featureCollection={featureCollection}
          overlayFeatureCollections={overlayFeatureCollections}
          overlaySettings={localOverlaySettings}
          isOverlayVisibleByName={isOverlayVisibleByName}
          geoJsonFeaturesLoader={geoJsonFeaturesLoader}
          organization={organization}
          firebaseToken={firebaseToken}
          fitPadding={fitPadding}
          isCameraReady={captionHeight !== null}
          mapStyle={mapStyle}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
        />
        <div className={cs.overlayControlsWrapper}>
          {activeTool === 'manageLayers' && (
            <ManageLayersDialog
              disableConfiguration
              onClose={() => setActiveTool(null)}
              overlaySettings={localOverlaySettings}
              setOverlaySettings={setLocalOverlaySettings}
              isOverlayVisibleByName={isOverlayVisibleByName}
              setIsOverlayVisibleByName={setOverlayVisibility}
              overlayFeatureCollections={overlayFeatureCollections}
            />
          )}
          {activeTool === 'manageBasemap' && (
            <ManageBasemapDialog
              onClose={() => setActiveTool(null)}
              onChangeMapStyle={setMapStyle}
              selectedStyle={mapStyle}
            />
          )}
        </div>
        <div className={cs.captionWrapper}>
          <StoryCaption
            ref={captionRef}
            slide={slide}
            slideNumber={slideIndex + 1}
            slideCount={slides.length}
            showFeatureName={isMultilocation}
            imageRefs={imageRefs}
            organization={organization}
            onPrevious={goToPrevious}
            onNext={goToNext}
            isCollapsed={isCaptionCollapsed}
            onToggleCollapsed={() => setIsCaptionCollapsed((collapsed) => !collapsed)}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Layer, date and source for each scene on the map, in the story bar.
 */
const SceneChips = React.memo(function SceneChips({
  imageRefs,
  featureData,
  organization,
}: {
  imageRefs: mapUtils.MapImageRefs;
  featureData: I.ImmutableOf<ApiFeatureData[]> | null;
  organization: I.ImmutableOf<ApiOrganization>;
}) {
  const refs = imageRefs.filter((ref) => ref.cursor);
  if (refs.length === 0) {
    return null;
  }

  return (
    <div className={cs.sceneChips}>
      {refs.map((ref, i) => {
        const layer = layerUtils.getLayer(ref.layerKey);
        const datum = featureData?.find(
          (d) => d!.get('date') === ref.cursor && d!.get('types').includes(ref.layerKey)
        );
        const details = datum
          ? featureUtils.imageAndSourceDetails(datum, ref.layerKey, organization).sourceDetails
          : null;
        const resolution = details?.resolution
          ? ` (${parseFloat(details.resolution.toFixed(3))}m)`
          : '';

        return (
          <React.Fragment key={`${ref.layerKey}-${ref.cursor}`}>
            {i > 0 && <B.NavbarDivider />}
            <div className={cs.sceneChip}>
              <div className={cs.sceneChipType}>
                {layer.shortName} | {imageryUtils.formatDate(moment(ref.cursor), ref.layerKey)}
              </div>
              {details && (
                <div className={cs.sceneChipName}>
                  {details.operator} {details.name}
                  {resolution}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
});

export default StoryView;
